import {
  Badge,
  Box,
  Button,
  Switch as ChakraSwitch,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { useContext, useEffect, useState } from 'react'
import { RiAddLine, RiDeleteBin6Line, RiPencilLine } from 'react-icons/ri'
import { useMutation } from 'react-query'
import { Link } from 'react-router-dom'

import { Modal } from '../../components/Modal'
import { Pagination } from '../../components/Pagination'
import { SearchContext } from '../../context/SearchContext'
import { PaymentCondition } from '../../Interfaces/paymentCondition'
import { api } from '../../services/api'
import { usePaymentConditions } from '../../services/hooks/usePaymentConditions'

const formatPercent = (value: number) => `${(value || 0).toFixed(2)}%`

const PaymentConditions = () => {
  const toast = useToast()
  const [page, setPage] = useState(1)

  const { queryContextParams, clearQuery } = useContext(SearchContext)

  const queryParams = queryContextParams ?? {}

  const { data, isLoading, isFetching, error, refetch } = usePaymentConditions({
    page,
    limit: 10,
    ...queryParams,
  })

  const { isOpen, onOpen, onClose } = useDisclosure()
  const [currentCondition, setCurrentCondition] = useState<PaymentCondition | null>(null)

  useEffect(() => {
    refetch()
  }, [queryContextParams])

  useEffect(() => {
    return () => {
      clearQuery()
    }
  }, [])

  const removeMutation = useMutation(
    async (paymentCondition: PaymentCondition) => {
      const response = await api.delete(`payment-conditions/${paymentCondition.id}`)
      toast({ description: 'Condição de pagamento removida com sucesso', status: 'success' })
      await refetch()
      onClose()
      return response.data
    },
    {
      onError: ({ response }) => {
        const errors = response?.data?.errors || []
        if (errors.length) {
          errors.forEach((err) => toast({ description: err.message, status: 'error' }))
        } else {
          toast({ description: 'Não foi possível remover a condição de pagamento', status: 'error' })
        }
      },
    },
  )

  const toggleStatusMutation = useMutation(async (paymentCondition: PaymentCondition) => {
    const response = await api.patch(`payment-conditions/${paymentCondition.id}/toggle-status`)
    toast({
      description: `Condição de pagamento ${response.data?.data?.is_active ? 'ativada' : 'desativada'} com sucesso`,
      status: 'success',
    })
    await refetch()
    return response.data
  })

  const handleDelete = (paymentCondition: PaymentCondition) => {
    setCurrentCondition(paymentCondition)
    onOpen()
  }

  const confirmDelete = () => {
    if (currentCondition && !removeMutation.isLoading) {
      removeMutation.mutate(currentCondition)
    }
  }

  return (
    <Box flex="1" borderRadius="8" bg="gray.800" p="8">
      <Flex mb="8" justifyContent="space-between" align="center">
        <Heading size="lg" fontWeight="normal">
          Condições de Pagamento
          {!isLoading && isFetching && <Spinner size="sm" color="gray.500" ml="4" />}
        </Heading>

        <Button
          as={Link}
          to="/payment-conditions/create"
          size="sm"
          fontSize="sm"
          colorScheme="pink"
          leftIcon={<Icon as={RiAddLine} fontSize="20" />}>
          Nova Condição
        </Button>
      </Flex>

      {isLoading ? (
        <Flex justify="center">
          <Spinner />
        </Flex>
      ) : error ? (
        <Flex justify="center">
          <Text>Falha ao obter as condições de pagamento</Text>
        </Flex>
      ) : (
        <Table colorScheme="whiteAlpha">
          <Thead>
            <Tr>
              <Th>Nome</Th>
              <Th>Parcelas</Th>
              <Th isNumeric>Dias p/ pagamento</Th>
              <Th isNumeric>Desconto</Th>
              <Th isNumeric>Juros</Th>
              <Th>Status</Th>
              <Th textAlign="right">Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {data?.paymentConditions.map((condition) => (
              <Tr key={condition.id}>
                <Td>
                  <Text fontWeight="bold">{condition.name}</Text>
                  {condition.description && (
                    <Text fontSize="sm" color="gray.300">
                      {condition.description}
                    </Text>
                  )}
                </Td>
                <Td>
                  <Badge colorScheme="purple">{condition.installments}x</Badge>
                </Td>
                <Td isNumeric>{Number(condition.day_to_payment ?? 0)}</Td>
                <Td isNumeric>{formatPercent(Number(condition.discount_percentage ?? 0))}</Td>
                <Td isNumeric>{formatPercent(Number(condition.interest_percentage ?? 0))}</Td>
                <Td>
                  <HStack spacing={3}>
                    <ChakraSwitch
                      colorScheme="pink"
                      isChecked={!!condition.is_active}
                      onChange={() => toggleStatusMutation.mutate(condition)}
                      isDisabled={toggleStatusMutation.isLoading}
                    />
                    <Text>{condition.is_active ? 'Ativo' : 'Inativo'}</Text>
                  </HStack>
                </Td>
                <Td textAlign="right">
                  <HStack spacing={2} justify="flex-end">
                    <IconButton
                      as={Link}
                      to={`/payment-conditions/edit/${condition.id}`}
                      aria-label="Editar"
                      icon={<RiPencilLine />}
                      size="sm"
                      colorScheme="purple"
                    />
                    <IconButton
                      aria-label="Remover"
                      icon={<RiDeleteBin6Line />}
                      size="sm"
                      colorScheme="red"
                      onClick={() => handleDelete(condition)}
                      isLoading={removeMutation.isLoading && currentCondition?.id === condition.id}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      <Pagination totalCountOfRegisters={data?.totalCount} currentPage={page} onPageChange={setPage} />

      <Modal
        isOpen={isOpen}
        onClose={() => {
          setCurrentCondition(null)
          onClose()
        }}
        title="Remover Condição de Pagamento"
        buttonTitle="Remover"
        buttonAction={confirmDelete}>
        <VStack spacing={3} align="stretch">
          <Text>
            Tem certeza que deseja remover a condição de pagamento <strong>{currentCondition?.name}</strong>?
          </Text>
          <Text fontSize="sm" color="gray.300">
            Esta ação não poderá ser desfeita.
          </Text>
        </VStack>
      </Modal>
    </Box>
  )
}

export default PaymentConditions
